import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import addIcon from "assets/images/add-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";

export const AppReminderTemplateModal = (props, ref) => {
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const [template, setTemplate] = useState(null);

    const label = useMemo(() => (
        template ? "Edit Reminder Template" : "Create Reminder Template"
    ), [template]);

    const buttonLabel = useMemo(() => (
        template ? "Save" : "Create"
    ), [template]);

    const initialValues = useMemo(() => ({
        id: template?.id || "",
        companyId: props.companyId,
        templateName: template?.templateName || "",
        isActive: template?.isActive !== undefined ? template.isActive : true,
        reminderDays: template?.reminderDays?.map(day => ({
            id: day.id || null,
            reminderDays: day.reminderDays,
        })) || [{ id: null, reminderDays: "" }],
    }), [template, props.companyId]);

    //prettier-ignore
    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: yup.object({
            templateName: yup.string().required(ERRORS.REQUIRED),
            reminderDays: yup.array()
                .of(yup.object({
                    reminderDays: yup.number()
                        .required(ERRORS.REQUIRED)
                        .min(1, ERRORS.MINIMUM_NUMBER)
                        .integer(ERRORS.NUMBERS_ONLY),
                }))
                .min(1, ERRORS.MINIMUM_DAY)
                .test("unique-days", ERRORS.UNIQUE, function (days) {
                    const dayValues = days.map(d => d.reminderDays);
                    const uniqueDays = new Set(dayValues.filter(v => v !== "" && v !== null && v !== undefined));
                    return uniqueDays.size === dayValues.filter(v => v !== "" && v !== null && v !== undefined).length;
                }),
        }),
        onSubmit: (values) => {
            onHandleConfirm(values);
        },
    });

    const onHandleShow = useCallback((obj) => {
        if (obj && obj.id) {
            setTemplate(obj);
        } else {
            setTemplate(null);
        }
        setVisible(true);
    }, []);

    const onHandleDismiss = useCallback(() => {
        formik.resetForm();
        setTemplate(null);
        setVisible(false);
    }, [formik]);

    const onHandleAddDay = useCallback(() => {
        formik.setFieldValue("reminderDays", [
            ...formik.values.reminderDays,
            { id: null, reminderDays: "" }
        ]);
    }, [formik]);

    const onHandleRemoveDay = useCallback((index) => {
        if (formik.values.reminderDays.length === 1) {
            dispatch(promptAlertMessage({ message: "At least one reminder day is required", error: true }));
            return;
        }

        const updated = formik.values.reminderDays.filter((_, i) => i !== index);
        formik.setFieldValue("reminderDays", updated);
    }, [formik, dispatch]);

    const onHandleDayChange = useCallback((index, field, value) => {
        const updated = [...formik.values.reminderDays];
        updated[index] = { ...updated[index], [field]: value };
        formik.setFieldValue("reminderDays", updated);
    }, [formik]);

    //prettier-ignore
    const onHandleConfirm = useCallback(async (values) => {
        let response = false;

        try {
            const payload = {
                companyId: values.companyId,
                templateName: values.templateName,
                isActive: values.isActive,
                reminderDays: values.reminderDays.map(day => ({
                    reminderDays: parseInt(day.reminderDays)
                })),
            };

            if (values.id) {
                payload.id = values.id
                response = await api.post.reminderTemplate.update(payload);
            } else {
                response = await api.post.reminderTemplate.create(payload);
            }

            if (response) {
                onHandleDismiss();
                dispatch(promptAlertMessage({
                    message: values.id
                        ? "Reminder template has been updated successfully"
                        : "Reminder template has been created successfully"
                }));
                props.onHandleGetList();
            }
        } catch (error) {
            serveRequestErrors(error);
        } finally {
            formik.setSubmitting(false);
        }
    }, [dispatch, formik, props, onHandleDismiss]);

    // Helper text for day conversion
    const getDayDisplayText = useCallback((days) => {
        const numDays = parseInt(days);
        if (!numDays || isNaN(numDays)) return "";

        if (numDays >= 30) {
            const months = Math.floor(numDays / 30);
            if (numDays % 30 === 0) {
                return ` (≈ ${months} month${months > 1 ? "s" : ""})`;
            }
        } else if (numDays >= 7) {
            const weeks = Math.floor(numDays / 7);
            if (numDays % 7 === 0) {
                return ` (≈ ${weeks} week${weeks > 1 ? "s" : ""})`;
            }
        }
        return "";
    }, []);

    //prettier-ignore
    useImperativeHandle(ref, () => ({
        onHandleShow: onHandleShow,
        onHandleDismiss: onHandleDismiss,
    }));

    return (
        <Modal
            classes={{ root: "app-reminder-template-modal" }}
            open={visible}
            onClose={onHandleDismiss}
            aria-labelledby="reminder-template-modal"
            aria-describedby="reminder-template-modal"
        >
            <div className="reminder-template-modal">
                <div className="reminder-template-modal__main">
                    <div className="reminder-template-modal__header">
                        <p className="reminder-template-modal__title">{label}</p>
                    </div>

                    <form className="reminder-template-modal__body" onSubmit={formik.handleSubmit}>

                        {/* prettier-ignore */}
                        <AppInput
                            required
                            type="text"
                            name="templateName"
                            label="Template Name"
                            placeholder="e.g. Standard Compliance"
                            value={formik.values.templateName}
                            error={formik.errors.templateName}
                            touched={formik.touched.templateName}
                            disabled={formik.isSubmitting}
                            onChange={formik.handleChange}
                        />

                        {/* Reminder Days Section */}
                        <div className="reminder-template-modal__days-section">
                            <label className="app-input__label">
                                Reminder Days <span className="app-input__required">*</span>
                            </label>
                            <p className="reminder-template-modal__description">
                                Set how many days before the assignment end date to send reminders.
                            </p>

                            <div className="reminder-template-modal__days-list">
                                {formik.values.reminderDays.map((day, index) => (
                                    <div key={index} className="reminder-template-modal__day-row">
                                        <div className="reminder-template-modal__day-input-group">
                                            <input
                                                type="number"
                                                min="1"
                                                className="reminder-template-modal__day-input"
                                                placeholder="Days"
                                                value={day.reminderDays}
                                                onChange={(e) => onHandleDayChange(index, "reminderDays", e.target.value)}
                                                disabled={formik.isSubmitting}
                                            />
                                            <span className="reminder-template-modal__day-label">
                                                days{getDayDisplayText(day.reminderDays)}
                                            </span>
                                        </div>
                                        {formik.touched.reminderDays?.[index]?.reminderDays &&
                                            formik.errors.reminderDays?.[index]?.reminderDays && (
                                                <div className="reminder-template-modal__error">
                                                    {formik.errors.reminderDays[index].reminderDays}
                                                </div>
                                            )}

                                        <button
                                            type="button"
                                            className="reminder-template-modal__remove-btn"
                                            onClick={() => onHandleRemoveDay(index)}
                                            disabled={formik.isSubmitting}
                                        >
                                            <img src={removeIcon} alt="remove" />
                                        </button>
                                    </div>
                                ))}

                            </div>


                            <button
                                type="button"
                                className="reminder-template-modal__add-btn"
                                onClick={onHandleAddDay}
                                disabled={formik.isSubmitting}
                            >
                                <img src={addIcon} alt="add" />
                                Add Reminder Day
                            </button>
                        </div>

                        <div className="reminder-template-modal__button-container">
                            <AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
                            <AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default memo(forwardRef(AppReminderTemplateModal));

AppReminderTemplateModal.propTypes = {
    ref: PropTypes.object,
    companyId: PropTypes.number.isRequired,
    onHandleGetList: PropTypes.func.isRequired,
};